/**
 * Grid Control — Pure Helper Functions
 * No React, no side-effects. All functions are deterministic.
 */

import type { GridColumn, GridRow, GridValidationErrors, ColumnValidation, LinkAction, LinkActionLeaf } from './grid.types';

// ─── CSS class evaluation ─────────────────────────────────────────────────────

/**
 * Evaluates a cellClassExpr string as a JS function.
 * Signature of the expression: (value, row) => string
 * Returns '' on any error — never throws.
 */
export const evaluateCellClass = (
  expr: string,
  value: unknown,
  row: Record<string, unknown>,
): string => {
  try {
    // eslint-disable-next-line no-new-func
    return String(new Function('value', 'row', `return (${expr})`)(value, row) ?? '');
  } catch {
    return '';
  }
};

/**
 * Evaluates a computed column expression.
 * Expression receives the full row as `row`.
 * Returns '' on any error — never throws.
 */
export const evaluateCompute = (expr: string, row: Record<string, unknown>): unknown => {
  try {
    // eslint-disable-next-line no-new-func
    return new Function('row', `return (${expr})`)(row);
  } catch {
    return '';
  }
};

// ─── Deep get / set ───────────────────────────────────────────────────────────

/**
 * Reads a value at a dot-notation path from an object.
 * Returns undefined if any segment is missing.
 */
export const getDeep = (obj: unknown, path: string): unknown =>
  path.split('.').reduce<unknown>((acc, key) => {
    if (acc !== null && acc !== undefined && typeof acc === 'object') {
      return (acc as Record<string, unknown>)[key];
    }
    return undefined;
  }, obj);

/**
 * Immutably sets a value at a dot-notation path.
 * Creates intermediate objects as needed.
 */
export const setDeep = (
  obj: Record<string, unknown>,
  path: string,
  value: unknown,
): Record<string, unknown> => {
  const [head, ...rest] = path.split('.');
  if (rest.length === 0) {
    return { ...obj, [head]: value };
  }
  const nested = obj[head] && typeof obj[head] === 'object' && !Array.isArray(obj[head])
    ? (obj[head] as Record<string, unknown>)
    : {};
  return { ...obj, [head]: setDeep(nested, rest.join('.'), value) };
};

// ─── Cell value helpers ───────────────────────────────────────────────────────

export const getCellValue = (row: Record<string, unknown>, col: GridColumn): unknown => {
  if (col.dataBinding?.fieldPath) {
    return getDeep(row, col.dataBinding.fieldPath);
  }
  return row[col.key];
};

export const setCellValue = (
  row: Record<string, unknown>,
  col: GridColumn,
  value: unknown,
): Record<string, unknown> => {
  if (col.dataBinding?.fieldPath) {
    return setDeep(row, col.dataBinding.fieldPath, value);
  }
  return { ...row, [col.key]: value };
};

// ─── Row ID ───────────────────────────────────────────────────────────────────

let _rowCounter = 0;

export const generateRowId = (): string => {
  _rowCounter += 1;
  return `_row_${Date.now()}_${_rowCounter}`;
};

// ─── Serialisation ────────────────────────────────────────────────────────────

const RESERVED_PREFIX = '_';

/**
 * Strips internal fields and computed/selector columns from a row
 * before it is returned as form output data.
 */
export const serializeRow = (
  row: GridRow,
  columns: GridColumn[],
  idField?: string,
): Record<string, unknown> => {
  const excludedKeys = new Set<string>();
  excludedKeys.add('_rowId');
  if (idField) excludedKeys.add(idField);

  for (const col of columns) {
    if (!col.key) continue;
    if (col.type === 'computed' || col.type === 'selector' || col.type === 'link') {
      excludedKeys.add(col.key);
    }
    if (col.key.startsWith(RESERVED_PREFIX)) {
      excludedKeys.add(col.key);
    }
  }

  const result: Record<string, unknown> = {};
  for (const [key, val] of Object.entries(row)) {
    if (!excludedKeys.has(key) && !key.startsWith(RESERVED_PREFIX)) {
      result[key] = val;
    }
  }
  return result;
};

// ─── New row factory ──────────────────────────────────────────────────────────

/**
 * Creates a new row by merging the newEntityTemplate with column defaults,
 * then stamping a fresh _rowId.
 *
 * Also pre-initialises intermediate objects for columns with a dot-notation
 * dataBinding.fieldPath (e.g. "address.city" → ensures `address: {}` exists)
 * so that any code reading the path never encounters a null/undefined parent.
 */
export const createNewRow = (
  template: Record<string, unknown> = {},
  columns: GridColumn[] = [],
): GridRow => {
  let base: Record<string, unknown> = { ...template };
  for (const col of columns) {
    if (col.default !== undefined && !(col.key in base)) {
      base[col.key] = col.default;
    }
    // Auto-create intermediate objects for nested binding paths
    if (col.dataBinding?.fieldPath) {
      const parts = col.dataBinding.fieldPath.split('.');
      if (parts.length > 1) {
        const parentPath = parts.slice(0, -1).join('.');
        const existing = getDeep(base, parentPath);
        if (existing === undefined || existing === null) {
          base = setDeep(base, parentPath, {});
        }
      }
    }
  }
  return { ...base, _rowId: generateRowId() } as GridRow;
};

/**
 * Stamps a _rowId onto an existing data object (used when hydrating from external data).
 */
export const stampRowId = (obj: Record<string, unknown>): GridRow =>
  ({ ...obj, _rowId: generateRowId() }) as GridRow;

// ─── Validation ───────────────────────────────────────────────────────────────

export const validateCell = (
  value: unknown,
  col: GridColumn,
): string | null => {
  // Required check applies regardless of col.validation
  if (col.required && (value === undefined || value === null || value === '')) {
    return 'This field is required';
  }

  const v = col.validation;
  if (!v) return null;
  const strVal = String(value ?? '');
  const numVal = Number(value);

  if (col.type === 'text' || col.type === undefined) {
    if (v.minLength !== undefined && strVal.length < v.minLength) {
      return v.message ?? `Minimum ${v.minLength} characters`;
    }
    if (v.maxLength !== undefined && strVal.length > v.maxLength) {
      return v.message ?? `Maximum ${v.maxLength} characters`;
    }
  }
  if (col.type === 'number') {
    if (v.min !== undefined && numVal < v.min) {
      return v.message ?? `Minimum value is ${v.min}`;
    }
    if (v.max !== undefined && numVal > v.max) {
      return v.message ?? `Maximum value is ${v.max}`;
    }
  }
  return null;
};

/**
 * Validates all editable cells in all rows.
 * Returns an errors map; empty object means valid.
 */
export const validateAllRows = (
  rows: GridRow[],
  columns: GridColumn[],
): GridValidationErrors => {
  const errors: GridValidationErrors = {};
  for (const row of rows) {
    for (const col of columns) {
      if (!col.key) continue;
      if (col.type === 'computed' || col.type === 'selector' || col.type === 'link') continue;
      const value = getCellValue(row, col);
      // validateCell already handles the required check — no duplicate needed
      const error = validateCell(value, col);
      if (error) {
        errors[row._rowId] = errors[row._rowId] ?? {};
        errors[row._rowId][col.key] = error;
      }
    }
  }
  return errors;
};

// ─── CSV / JSON export ────────────────────────────────────────────────────────

export const exportToJson = (
  rows: GridRow[],
  columns: GridColumn[],
  idField?: string,
): string => {
  const data = rows.map((r) => serializeRow(r, columns, idField));
  return JSON.stringify(data, null, 2);
};

export const exportToCsv = (
  rows: GridRow[],
  columns: GridColumn[],
  idField?: string,
): string => {
  const dataColumns = columns.filter(
    (c) => c.key && c.type !== 'computed' && c.type !== 'selector' && c.type !== 'link' && !c.key.startsWith('_'),
  );
  const header = dataColumns.map((c) => `"${c.label || c.key}"`).join(',');
  const bodyLines = rows.map((row) => {
    return dataColumns
      .map((col) => {
        const val = getCellValue(row, col) ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      })
      .join(',');
  });
  return [header, ...bodyLines].join('\n');
};

// ─── JSON / CSV import ────────────────────────────────────────────────────────

export const importFromJson = (text: string): Record<string, unknown>[] => {
  try {
    const parsed = JSON.parse(text);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter((item) => item && typeof item === 'object');
  } catch {
    return [];
  }
};

export const importFromCsv = (text: string): Record<string, unknown>[] => {
  const lines = text.trim().split('\n');
  if (lines.length < 2) return [];
  const headers = lines[0].split(',').map((h) => h.replace(/^"|"$/g, '').trim());
  return lines.slice(1).map((line) => {
    const cells = line.split(',').map((c) => c.replace(/^"|"$/g, ''));
    const row: Record<string, unknown> = {};
    headers.forEach((h, i) => {
      row[h] = cells[i] ?? '';
    });
    return row;
  });
};

// ─── Drag reorder ─────────────────────────────────────────────────────────────

export const reorderRows = <T>(
  rows: T[],
  fromIndex: number,
  toIndex: number,
): T[] => {
  if (fromIndex === toIndex) return rows;
  const result = [...rows];
  const [moved] = result.splice(fromIndex, 1);
  result.splice(toIndex, 0, moved);
  return result;
};

// ─── Pagination ───────────────────────────────────────────────────────────────

export const getPage = <T>(items: T[], page: number, pageSize: number): T[] => {
  const start = (page - 1) * pageSize;
  return items.slice(start, start + pageSize);
};

export const totalPages = (total: number, pageSize: number): number =>
  Math.max(1, Math.ceil(total / pageSize));

// ─── Link column helpers ──────────────────────────────────────────────────────

/**
 * Replaces {{row.fieldName}} tokens in a template string with row values. Supports dot-notation
 * paths. `encodeValues` URL-encodes each interpolated VALUE (not the static template text) before
 * insertion -- required whenever the result is used as a real request URL (e.g. the `delete`
 * action's `endpointTemplate`), so a row value containing `/`, `..`, `?`, `#`, etc. can't redirect
 * the request to an unintended path/endpoint. Left off by default for template kinds where the
 * result is plain display text (e.g. `confirmMessage`) or an intentionally free-form URL/target
 * (`url` kind's own `urlTemplate`), where encoding would corrupt the intended value.
 */
export const interpolateTemplate = (
  template: string,
  row: Record<string, unknown>,
  encodeValues = false,
): string =>
  template.replace(/\{\{row\.([^}]+)\}\}/g, (_, key: string) => {
    const value = String(getDeep(row, key) ?? '');
    return encodeValues ? encodeURIComponent(value) : value;
  });

export type ResolvedLink =
  | {
      kind: 'form';
      formId: number;
      target?: string;
      mode?: 'view' | 'edit';
      saveEndpoint?: string;
      saveMethod?: 'PUT' | 'POST';
      dataRootPath?: string;
    }
  | { kind: 'url'; url: string; target?: string }
  | { kind: 'delete'; endpoint: string; method: 'DELETE' | 'POST' | 'PUT'; confirmMessage?: string };

const resolveLeaf = (
  action: LinkActionLeaf,
  row: Record<string, unknown>,
): ResolvedLink | null => {
  switch (action.kind) {
    case 'form':
      return {
        kind: 'form',
        formId: action.formId,
        target: action.target,
        mode: action.mode,
        // encodeValues: true -- this becomes a real fetch() URL, same as `delete`'s endpoint.
        saveEndpoint: action.saveEndpointTemplate
          ? interpolateTemplate(action.saveEndpointTemplate, row, true)
          : undefined,
        saveMethod: action.saveMethod,
        dataRootPath: action.dataRootPath,
      };

    case 'url':
      return { kind: 'url', url: interpolateTemplate(action.urlTemplate, row), target: action.target };

    case 'delete':
      return {
        kind: 'delete',
        // encodeValues: true -- this becomes a real fetch() URL, unlike confirmMessage (plain
        // display text) or the `url` kind's own urlTemplate (an intentionally free-form target).
        endpoint: interpolateTemplate(action.endpointTemplate, row, true),
        method: action.method ?? 'DELETE',
        confirmMessage: action.confirmMessage ? interpolateTemplate(action.confirmMessage, row) : undefined,
      };

    case 'expression': {
      try {
        // eslint-disable-next-line no-new-func
        const result: unknown = new Function('row', `return (${action.expression})`)(row);
        if (!result) return null;
        if (typeof result === 'number' && result > 0) return { kind: 'form', formId: result };
        if (typeof result === 'string' && result) return { kind: 'url', url: result };
        if (typeof result === 'object') {
          const r = result as Record<string, unknown>;
          if ('formId' in r) {
            const fid = Number(r.formId);
            if (fid > 0) return { kind: 'form', formId: fid };
          }
          if ('url' in r && r.url) return { kind: 'url', url: String(r.url) };
        }
      } catch (err) {
        console.error('[GridControl] Link expression error:', err, '\nExpression:', action.expression);
      }
      return null;
    }
  }
};

/**
 * Resolves a LinkAction against the given row.
 * Returns the destination (form or URL), or null if nothing matched.
 * Never throws.
 */
export const resolveLinkAction = (
  action: LinkAction,
  row: Record<string, unknown>,
): ResolvedLink | null => {
  if (action.kind !== 'matrix') return resolveLeaf(action, row);

  for (const rule of action.rules) {
    try {
      // eslint-disable-next-line no-new-func
      const match = Boolean(new Function('row', `return !!(${rule.condition})`)(row));
      if (match) return resolveLeaf(rule.action, row);
    } catch { /* skip bad condition */ }
  }
  return resolveLeaf(action.fallback, row);
};
